import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, postData, postDataWithForm } from "../../services/request"
import {
  PREDEFINED_METRICS_FALLBACK,
  apiErrorMessage,
  equalWeights,
  normalizeMetricWeights,
} from "./cvScreeningUtils"

const STEPS = [
  { key: "role", label: "Role" },
  { key: "metrics", label: "Metrics" },
  { key: "upload", label: "Upload" },
  { key: "review", label: "Start" },
]

const MAX_FILES = 50
const MAX_FILE_MB = 10
const ACCEPTED =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"

const formatBytes = (n) => {
  if (!n && n !== 0) return ""
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

const CvScreeningWizardPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectJobId = searchParams.get("jobId") || ""

  const [step, setStep] = useState(0)
  const [sourceMode, setSourceMode] = useState(preselectJobId ? "JOB_POST" : "JOB_POST")
  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobSearch, setJobSearch] = useState("")
  const [selectedJobId, setSelectedJobId] = useState(preselectJobId)
  const [roleForm, setRoleForm] = useState({
    title: "",
    description: "",
    requirements: "",
    skillsText: "",
  })
  const [aiBrief, setAiBrief] = useState("")
  const [aiLoading, setAiLoading] = useState(false)
  const [aiMsg, setAiMsg] = useState("")

  const [predefinedCatalog, setPredefinedCatalog] = useState(PREDEFINED_METRICS_FALLBACK)
  const [selectedKeys, setSelectedKeys] = useState(() =>
    PREDEFINED_METRICS_FALLBACK.map((m) => m.key)
  )
  const [customMetrics, setCustomMetrics] = useState([])
  const [customDraft, setCustomDraft] = useState({ label: "", description: "" })
  const [weightMode, setWeightMode] = useState("equal")
  const [metricWeights, setMetricWeights] = useState({})

  const [batchId, setBatchId] = useState(null)
  const [files, setFiles] = useState([])
  const [uploadedCount, setUploadedCount] = useState(0)
  const [uploadErrors, setUploadErrors] = useState([])
  const [dragOver, setDragOver] = useState(false)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    const loadJobs = async () => {
      setJobsLoading(true)
      const url = `${getAPIBaseURL()}/employme/employer/jobs`
      const res = await getData(url, {}, { status: "LIVE" })
      setJobsLoading(false)
      if (res.error === "" && res.result?.status === 200) {
        setJobs(res.result.data?.data || [])
      }
    }
    const loadMetrics = async () => {
      const url = `${getAPIBaseURL()}/employme/employer/cv-screening/metrics`
      const res = await getData(url, {}, {})
      if (res.error === "" && res.result?.status === 200) {
        const list = res.result.data?.data
        if (Array.isArray(list) && list.length) setPredefinedCatalog(list)
      }
    }
    loadJobs()
    loadMetrics()
  }, [])

  useEffect(() => {
    if (preselectJobId) {
      setSourceMode("JOB_POST")
      setSelectedJobId(preselectJobId)
    }
  }, [preselectJobId])

  const selectedJob = useMemo(
    () => jobs.find((j) => String(j._id) === String(selectedJobId)),
    [jobs, selectedJobId]
  )

  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase()
    if (!q) return jobs
    return jobs.filter((j) => (j.title || "").toLowerCase().includes(q))
  }, [jobs, jobSearch])

  const activeMetrics = useMemo(() => {
    const predefined = predefinedCatalog
      .filter((m) => selectedKeys.includes(m.key))
      .map((m) => ({
        key: m.key,
        label: m.label,
        description: m.description || "",
        type: "predefined",
        weight: Number(metricWeights[m.key]) || 0,
      }))
    const customs = customMetrics.map((m) => ({
      ...m,
      type: "custom",
      weight: Number(metricWeights[m.key]) || 0,
    }))
    return [...predefined, ...customs]
  }, [predefinedCatalog, selectedKeys, customMetrics, metricWeights])

  useEffect(() => {
    if (weightMode !== "equal") return
    const w = equalWeights(activeMetrics.length)
    const next = {}
    activeMetrics.forEach((m) => {
      next[m.key] = w
    })
    setMetricWeights(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightMode, selectedKeys.join(","), customMetrics.map((m) => m.key).join(",")])

  const weightPercentSum = useMemo(
    () =>
      Math.round(
        activeMetrics.reduce((s, m) => s + (Number(metricWeights[m.key]) || 0) * 100, 0)
      ),
    [activeMetrics, metricWeights]
  )

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || [])
    const next = [...files]
    const errs = []
    for (const f of incoming) {
      if (next.length >= MAX_FILES) {
        errs.push(`Max ${MAX_FILES} files per batch`)
        break
      }
      const ext = (f.name || "").toLowerCase()
      const okType =
        f.type.includes("pdf") ||
        f.type.includes("word") ||
        f.type.includes("msword") ||
        f.type.includes("officedocument") ||
        ext.endsWith(".pdf") ||
        ext.endsWith(".doc") ||
        ext.endsWith(".docx")
      if (!okType) {
        errs.push(`${f.name}: only PDF or Word files`)
        continue
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        errs.push(`${f.name}: exceeds ${MAX_FILE_MB}MB`)
        continue
      }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue
      next.push(f)
    }
    setFiles(next)
    if (errs.length) setError(errs.join(". "))
    else setError("")
  }

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  const validateRole = () => {
    if (sourceMode === "JOB_POST") {
      if (!selectedJobId) return "Select a live job"
      return ""
    }
    if (!roleForm.title.trim()) return "Role title is required"
    return ""
  }

  const validateMetrics = () => {
    if (!activeMetrics.length) return "Select at least one metric"
    if (weightMode === "custom" && weightPercentSum !== 100) {
      return "Weights must add up to 100%"
    }
    return ""
  }

  const ensureBatch = async () => {
    if (batchId) return batchId
    const roleErr = validateRole()
    if (roleErr) throw new Error(roleErr)
    const metricsErr = validateMetrics()
    if (metricsErr) throw new Error(metricsErr)

    const metricsPayload = normalizeMetricWeights(
      activeMetrics.map((m) => ({
        ...m,
        weight: Number(metricWeights[m.key]) || 0,
      }))
    )

    const payload =
      sourceMode === "JOB_POST"
        ? {
            sourceType: "JOB_POST",
            jobId: selectedJobId,
            metrics: metricsPayload,
          }
        : {
            sourceType: "SCREENING_ROLE",
            roleSnapshot: {
              title: roleForm.title.trim(),
              description: roleForm.description.trim(),
              requirements: roleForm.requirements.trim(),
              skills: roleForm.skillsText
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            },
            metrics: metricsPayload,
          }

    const url = `${getAPIBaseURL()}/employme/employer/cv-screening`
    const res = await postData(
      url,
      { "Content-Type": "application/json" },
      JSON.stringify(payload)
    )
    if (res.error !== "" || !(res.result?.status === 200 || res.result?.status === 201)) {
      throw new Error(apiErrorMessage(res, "Could not create screening batch"))
    }
    const created = res.result.data?.data
    const id = created?._id
    if (!id) throw new Error("Batch created without id")
    setBatchId(id)
    return id
  }

  const uploadPendingFiles = async (id) => {
    if (!files.length) throw new Error("Add at least one CV")
    const formData = new FormData()
    files.forEach((f) => formData.append("cvs", f))
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${id}/upload`
    const res = await postDataWithForm(url, {}, formData)
    if (res.error !== "" || res.result?.status !== 200) {
      throw new Error(apiErrorMessage(res, "Upload failed"))
    }
    const data = res.result.data?.data || {}
    setUploadedCount((data.uploaded || []).length)
    setUploadErrors(data.errors || [])
    if (!(data.uploaded || []).length) {
      throw new Error(
        (data.errors || []).map((e) => e.error || e.fileName).join("; ") || "No files uploaded"
      )
    }
    setFiles([])
    return data
  }

  const goNext = async () => {
    setError("")
    if (step === 0) {
      const err = validateRole()
      if (err) {
        setError(err)
        return
      }
      setStep(1)
      return
    }
    if (step === 1) {
      const err = validateMetrics()
      if (err) {
        setError(err)
        return
      }
      setBusy(true)
      try {
        await ensureBatch()
        setStep(2)
      } catch (e) {
        setError(e.message || "Could not save batch")
      } finally {
        setBusy(false)
      }
      return
    }
    if (step === 2) {
      if (!files.length && uploadedCount === 0) {
        setError("Add at least one CV")
        return
      }
      if (files.length) {
        setBusy(true)
        try {
          const id = await ensureBatch()
          await uploadPendingFiles(id)
          setStep(3)
        } catch (e) {
          setError(e.message || "Upload failed")
        } finally {
          setBusy(false)
        }
        return
      }
      setStep(3)
      return
    }
  }

  const startAnalysis = async () => {
    setError("")
    setBusy(true)
    try {
      let id = batchId
      if (!id) id = await ensureBatch()
      let justUploaded = 0
      if (files.length) {
        const data = await uploadPendingFiles(id)
        justUploaded = (data.uploaded || []).length
      }
      if (uploadedCount === 0 && justUploaded === 0) {
        throw new Error("Upload at least one CV first")
      }
      const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${id}/start`
      const res = await postData(url, { "Content-Type": "application/json" }, JSON.stringify({}))
      if (res.error !== "" || res.result?.status !== 200) {
        throw new Error(apiErrorMessage(res, "Could not start analysis"))
      }
      navigate(`/employer/cv-screening/${id}`)
    } catch (e) {
      setError(e.message || "Could not start analysis")
    } finally {
      setBusy(false)
    }
  }

  const onDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    addFiles(e.dataTransfer?.files)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const toMultiline = (value) => {
    if (!value) return ""
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean).join("\n")
    }
    return String(value)
  }

  const toCommaList = (value) => {
    if (!value) return ""
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean).join(", ")
    }
    return String(value)
  }

  const applyRoleDraft = (draft) => {
    if (!draft || typeof draft !== "object") return
    const descriptionParts = [
      draft.description ? String(draft.description).trim() : "",
      Array.isArray(draft.responsibilities) && draft.responsibilities.length
        ? `Responsibilities:\n${draft.responsibilities.map((r) => `• ${String(r).trim()}`).join("\n")}`
        : "",
    ].filter(Boolean)

    setRoleForm((prev) => ({
      ...prev,
      title: (draft.title && String(draft.title).trim()) || prev.title,
      description: descriptionParts.join("\n\n") || prev.description,
      requirements: toMultiline(draft.requirements) || prev.requirements,
      skillsText: toCommaList(draft.skills || draft.preferredSkills) || prev.skillsText,
    }))
  }

  const handleGenerateRoleWithAI = async () => {
    setError("")
    setAiMsg("")
    if (!roleForm.title.trim() && !aiBrief.trim()) {
      setError("Enter a role title or a short brief before generating")
      return
    }
    setAiLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/ai/screening-role`
      const payload = {
        roleTitle: roleForm.title.trim(),
        summary: aiBrief.trim() || roleForm.description.trim(),
        description: roleForm.description.trim(),
        requirements: roleForm.requirements.trim(),
        skills: roleForm.skillsText.trim(),
      }
      const res = await postData(url, { "Content-Type": "application/json" }, JSON.stringify(payload))
      if (res.error === "" && res.result?.status === 200) {
        const draft = res.result.data?.data
        if (draft) {
          applyRoleDraft(draft)
          setAiMsg("AI draft applied. Review and edit before uploading CVs.")
        } else {
          setError("AI did not return a usable draft")
        }
      } else {
        setError(apiErrorMessage(res, "Could not generate role details"))
      }
    } finally {
      setAiLoading(false)
    }
  }

  const handleImproveRoleWithAI = async () => {
    setError("")
    setAiMsg("")
    if (!roleForm.description.trim() && !roleForm.requirements.trim()) {
      setError("Add a description or requirements to improve")
      return
    }
    setAiLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/ai/job-improve`
      const payload = {
        title: roleForm.title.trim(),
        description: roleForm.description.trim(),
        requirements: roleForm.requirements.trim(),
        skills: roleForm.skillsText.trim(),
      }
      const res = await postData(url, { "Content-Type": "application/json" }, JSON.stringify(payload))
      if (res.error === "" && res.result?.status === 200) {
        const improved = res.result.data?.data
        if (improved) {
          applyRoleDraft(improved)
          setAiMsg("AI improved this screening role. Review before continuing.")
        } else {
          setError("AI did not return an improved draft")
        }
      } else {
        setError(apiErrorMessage(res, "Could not improve role details"))
      }
    } finally {
      setAiLoading(false)
    }
  }

  const addCustomMetric = () => {
    const label = customDraft.label.trim()
    if (!label) {
      setError("Custom metric needs a label")
      return
    }
    const key = `custom_${label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "")
      .slice(0, 40)}`
    if (activeMetrics.some((m) => m.key === key || m.label.toLowerCase() === label.toLowerCase())) {
      setError("That metric already exists")
      return
    }
    setCustomMetrics((prev) => [
      ...prev,
      { key, label, description: customDraft.description.trim() },
    ])
    setCustomDraft({ label: "", description: "" })
    setError("")
  }

  const roleTitlePreview =
    sourceMode === "JOB_POST"
      ? selectedJob?.title || "Selected job"
      : roleForm.title.trim() || "Screening role"

  return (
    <div className="mx-auto max-w-3xl animate-fade-in">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <Link
            to="/employer/cv-screening"
            className="text-xs font-medium text-cyan-700 hover:text-cyan-900"
          >
            ← All screenings
          </Link>
          <h2 className="mt-1 text-xl font-semibold text-gray-900 sm:text-2xl">
            New CV screening
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Choose a role, pick metrics, upload CVs, then start AI analysis.
          </p>
        </div>
      </div>

      <ol className="mb-6 grid grid-cols-4 gap-2">
        {STEPS.map((s, i) => {
          const active = i === step
          const done = i < step
          return (
            <li key={s.key} className="min-w-0">
              <div
                className={`rounded-xl border px-2 py-2.5 text-center transition sm:px-3 ${
                  active
                    ? "border-cyan-400 bg-cyan-50 shadow-sm"
                    : done
                    ? "border-emerald-200 bg-emerald-50/60"
                    : "border-gray-200 bg-white"
                }`}
              >
                <p
                  className={`text-[10px] font-semibold uppercase tracking-wide ${
                    active ? "text-cyan-700" : done ? "text-emerald-700" : "text-gray-400"
                  }`}
                >
                  Step {i + 1}
                </p>
                <p
                  className={`truncate text-xs font-semibold sm:text-sm ${
                    active ? "text-cyan-900" : done ? "text-emerald-900" : "text-gray-500"
                  }`}
                >
                  {s.label}
                </p>
              </div>
            </li>
          )
        })}
      </ol>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        {step === 0 ? (
          <div className="space-y-5 animate-slide-up">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Role source</h3>
              <p className="mt-1 text-sm text-gray-500">
                Attach a live job or define a screening-only role (not published).
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setSourceMode("JOB_POST")}
                className={`rounded-xl border px-4 py-4 text-left transition ${
                  sourceMode === "JOB_POST"
                    ? "border-cyan-400 bg-cyan-50 ring-1 ring-cyan-300"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="text-sm font-semibold text-gray-900">Live job</p>
                <p className="mt-1 text-xs text-gray-500">
                  Use an existing published job as the screening target.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setSourceMode("SCREENING_ROLE")}
                className={`rounded-xl border px-4 py-4 text-left transition ${
                  sourceMode === "SCREENING_ROLE"
                    ? "border-cyan-400 bg-cyan-50 ring-1 ring-cyan-300"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="text-sm font-semibold text-gray-900">Screening role</p>
                <p className="mt-1 text-xs text-gray-500">
                  Lightweight role for analysis only — no payment or approval.
                </p>
              </button>
            </div>

            {sourceMode === "JOB_POST" ? (
              <div className="space-y-3">
                <input
                  type="search"
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  placeholder="Search live jobs..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
                {jobsLoading ? (
                  <p className="text-sm text-gray-500">Loading live jobs...</p>
                ) : filteredJobs.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                    No live jobs found. Create a screening role instead, or publish a job first.
                  </div>
                ) : (
                  <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
                    {filteredJobs.map((job) => {
                      const selected = String(job._id) === String(selectedJobId)
                      return (
                        <li key={job._id}>
                          <button
                            type="button"
                            onClick={() => setSelectedJobId(job._id)}
                            className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                              selected
                                ? "border-cyan-400 bg-cyan-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <p className="text-sm font-semibold text-gray-900">{job.title}</p>
                            <p className="mt-0.5 text-[11px] text-gray-500">
                              {(
                                typeof job.location === "string"
                                  ? job.location
                                  : [job.location?.city, job.location?.state, job.location?.country]
                                      .filter(Boolean)
                                      .join(", ")
                              ) || "No location"}{" "}
                              · Live
                            </p>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        Auto-generate this screening role with AI
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Enter a role title or a short brief. AI fills the description, requirements, and skills used to score uploaded CVs. Edit anything before you continue.
                      </p>
                      <textarea
                        value={aiBrief}
                        onChange={(e) => setAiBrief(e.target.value)}
                        rows={3}
                        disabled={aiLoading}
                        className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:bg-gray-50"
                        placeholder="e.g. We need a senior backend engineer with Node.js and MongoDB experience for a fintech payments team..."
                      />
                      <p className="mt-2 text-[11px] text-gray-500">
                        AI suggestions may contain mistakes. Review for accuracy before screening candidates.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:w-48">
                      <button
                        type="button"
                        onClick={handleGenerateRoleWithAI}
                        disabled={aiLoading || busy}
                        className="w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:bg-gray-400"
                      >
                        {aiLoading ? "Generating..." : "Auto-generate with AI"}
                      </button>
                      <button
                        type="button"
                        onClick={handleImproveRoleWithAI}
                        disabled={aiLoading || busy}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {aiLoading ? "Working..." : "Improve my text"}
                      </button>
                    </div>
                  </div>
                  {aiMsg ? (
                    <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
                      {aiMsg}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Role title *
                  </label>
                  <input
                    value={roleForm.title}
                    onChange={(e) => setRoleForm((p) => ({ ...p, title: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    placeholder="e.g. Senior Backend Engineer"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm((p) => ({ ...p, description: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    placeholder="What the role involves..."
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Requirements
                  </label>
                  <textarea
                    rows={3}
                    value={roleForm.requirements}
                    onChange={(e) =>
                      setRoleForm((p) => ({ ...p, requirements: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    placeholder="Must-haves and nice-to-haves..."
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Skills (comma-separated)
                  </label>
                  <input
                    value={roleForm.skillsText}
                    onChange={(e) => setRoleForm((p) => ({ ...p, skillsText: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    placeholder="Node.js, MongoDB, AWS"
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="space-y-5 animate-slide-up">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Scoring metrics</h3>
              <p className="mt-1 text-sm text-gray-500">
                Choose predefined metrics and optionally add custom ones. Weights default to equal.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {predefinedCatalog.map((m) => {
                const on = selectedKeys.includes(m.key)
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() =>
                      setSelectedKeys((prev) =>
                        on ? prev.filter((k) => k !== m.key) : [...prev, m.key]
                      )
                    }
                    className={`rounded-xl border px-3 py-3 text-left transition ${
                      on
                        ? "border-cyan-400 bg-cyan-50 ring-1 ring-cyan-200"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">{m.label}</p>
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">{m.description}</p>
                  </button>
                )
              })}
            </div>

            <div className="rounded-xl border border-dashed border-gray-300 bg-slate-50/80 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Custom metric
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <input
                  value={customDraft.label}
                  onChange={(e) => setCustomDraft((p) => ({ ...p, label: e.target.value }))}
                  placeholder="Label"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  value={customDraft.description}
                  onChange={(e) =>
                    setCustomDraft((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Description (optional)"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={addCustomMetric}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black"
                >
                  Add
                </button>
              </div>
              {customMetrics.length ? (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {customMetrics.map((m) => (
                    <li
                      key={m.key}
                      className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-800 ring-1 ring-gray-200"
                    >
                      {m.label}
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800"
                        onClick={() =>
                          setCustomMetrics((prev) => prev.filter((x) => x.key !== m.key))
                        }
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900">Weights</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setWeightMode("equal")}
                    className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                      weightMode === "equal"
                        ? "bg-black text-white"
                        : "border border-gray-200 text-gray-700"
                    }`}
                  >
                    Equal
                  </button>
                  <button
                    type="button"
                    onClick={() => setWeightMode("custom")}
                    className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                      weightMode === "custom"
                        ? "bg-black text-white"
                        : "border border-gray-200 text-gray-700"
                    }`}
                  >
                    Custom
                  </button>
                </div>
              </div>
              {weightMode === "custom" ? (
                <div className="mt-3 space-y-3">
                  {activeMetrics.map((m) => (
                    <div key={m.key}>
                      <div className="mb-1 flex justify-between text-xs text-gray-600">
                        <span>{m.label}</span>
                        <span>{Math.round((Number(metricWeights[m.key]) || 0) * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={Math.round((Number(metricWeights[m.key]) || 0) * 100)}
                        onChange={(e) => {
                          const pct = Number(e.target.value) / 100
                          setMetricWeights((prev) => ({ ...prev, [m.key]: pct }))
                        }}
                        className="w-full accent-cyan-600"
                      />
                    </div>
                  ))}
                  <p
                    className={`text-xs font-medium ${
                      weightPercentSum === 100 ? "text-emerald-700" : "text-amber-700"
                    }`}
                  >
                    Total: {weightPercentSum}% {weightPercentSum === 100 ? "✓" : "(need 100%)"}
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-xs text-gray-500">
                  Each of {activeMetrics.length || 0} selected metrics gets equal weight.
                </p>
              )}
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-5 animate-slide-up">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Upload CVs</h3>
              <p className="mt-1 text-sm text-gray-500">
                PDF or Word · up to {MAX_FILES} files · {MAX_FILE_MB}MB each.
                {uploadedCount > 0
                  ? ` ${uploadedCount} already uploaded to this batch.`
                  : ""}
              </p>
            </div>
            <div
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`rounded-2xl border-2 border-dashed px-4 py-12 text-center transition ${
                dragOver
                  ? "border-cyan-400 bg-cyan-50"
                  : "border-gray-300 bg-slate-50/50 hover:border-cyan-300"
              }`}
            >
              <p className="text-sm font-semibold text-gray-900">Drag & drop CVs here</p>
              <p className="mt-1 text-xs text-gray-500">or browse from your computer</p>
              <label className="mt-4 inline-flex cursor-pointer rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900">
                Choose files
                <input
                  type="file"
                  multiple
                  accept={ACCEPTED}
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files)
                    e.target.value = ""
                  }}
                />
              </label>
            </div>
            {files.length ? (
              <ul className="max-h-56 space-y-2 overflow-y-auto">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${f.size}-${idx}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">{f.name}</p>
                      <p className="text-[11px] text-gray-500">{formatBytes(f.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="shrink-0 text-xs font-medium text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {uploadErrors.length ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Some files failed:{" "}
                {uploadErrors.map((e) => e.fileName || e.error).join(", ")}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-5 animate-slide-up">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Review & start</h3>
              <p className="mt-1 text-sm text-gray-500">
                Confirm details, then start AI analysis. You can follow progress on the next screen.
              </p>
            </div>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Role
                </dt>
                <dd className="mt-1 text-sm font-semibold text-gray-900">{roleTitlePreview}</dd>
                <dd className="text-xs text-gray-500">
                  {sourceMode === "JOB_POST" ? "From live job" : "Screening role"}
                </dd>
              </div>
              <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  CVs
                </dt>
                <dd className="mt-1 text-sm font-semibold text-gray-900">
                  {uploadedCount + files.length} file
                  {uploadedCount + files.length === 1 ? "" : "s"}
                </dd>
                <dd className="text-xs text-gray-500">
                  {files.length ? `${files.length} still to upload` : "Ready"}
                </dd>
              </div>
            </dl>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Metrics ({activeMetrics.length})
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {activeMetrics.map((m) => (
                  <li
                    key={m.key}
                    className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-900 ring-1 ring-cyan-100"
                  >
                    {m.label} · {Math.round((Number(metricWeights[m.key]) || 0) * 100)}%
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <button
            type="button"
            disabled={busy || step === 0}
            onClick={() => {
              setError("")
              setStep((s) => Math.max(0, s - 1))
            }}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Back
          </button>
          {step < 3 ? (
            <button
              type="button"
              disabled={busy}
              onClick={goNext}
              className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:opacity-50"
            >
              {busy ? "Working..." : "Continue"}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={startAnalysis}
              className="rounded-lg bg-cyan-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-700 disabled:opacity-50"
            >
              {busy ? "Starting..." : "Start analysis"}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default CvScreeningWizardPage
